<template>
  <div>

    <!-- ═══════════════════════ DESKTOP ═══════════════════════ -->
    <div class="mt-3">
      <appHeader></appHeader>
      <div class="container-fluid account p-4">

        <!-- Stepper -->
        <div class="cart-stepper mb-4">
          <div class="cs-step done">
            <div class="cs-dot"><font-awesome-icon icon="check" /></div>
            <span>Choose item</span>
          </div>
          <div class="cs-line cs-line-done"></div>
          <div class="cs-step active">
            <div class="cs-dot">2</div>
            <span>Shipping information</span>
          </div>
          <div class="cs-line"></div>
          <div class="cs-step">
            <div class="cs-dot">3</div>
            <span>Order summary</span>
          </div>
        </div>

        <transition appear name="fade">
          <b-form v-on:submit.prevent="" class="needs-validation">
            <div class="row d-flex justify-content-center">

              <!-- Left: form -->
              <div class="col-md-7 mb-4 pr-4">
                <div class="form-card">
                  <div class="form-section-title">Complete your information</div>
                  <p class="form-subtitle">Please enter the following information so we may process your order.</p>
                  <div class="form-section-label">Shipment address</div>

                  <div class="form-row-grid cols-2">
                    <div class="field" :class="{ 'has-error': errors.firstName }">
                      <label for="firstName">First name</label>
                      <input id="firstName" v-model="currentUserFirstname"
                        class="form-input" :class="fieldClass('firstName')"
                        type="text" placeholder="John"
                        @blur="touchField('firstName')" @input="touchField('firstName')" />
                      <div class="field-error" v-if="errors.firstName">
                        <font-awesome-icon icon="exclamation-circle" /> First name is required
                      </div>
                    </div>
                    <div class="field" :class="{ 'has-error': errors.lastName }">
                      <label for="lastName">Last name</label>
                      <input id="lastName" v-model="currentUserLastname"
                        class="form-input" :class="fieldClass('lastName')"
                        type="text" placeholder="Doe"
                        @blur="touchField('lastName')" @input="touchField('lastName')" />
                      <div class="field-error" v-if="errors.lastName">
                        <font-awesome-icon icon="exclamation-circle" /> Last name is required
                      </div>
                    </div>
                  </div>

                  <div class="form-row-grid cols-2">
                    <div class="field" :class="{ 'has-error': errors.address1 }">
                      <label for="homeAddress1">Address line 1</label>
                      <input id="homeAddress1" v-model="currentUserAddress1"
                        class="form-input" :class="fieldClass('address1')"
                        type="text" placeholder="1234 Main St"
                        @blur="touchField('address1')" @input="touchField('address1')" />
                      <div class="field-error" v-if="errors.address1">
                        <font-awesome-icon icon="exclamation-circle" /> Address is required
                      </div>
                    </div>
                    <div class="field">
                      <label for="homeAddress2">Address line 2 <span class="opt">(Optional)</span></label>
                      <input id="homeAddress2" v-model="currentUserAddress2"
                        class="form-input" type="text" placeholder="Apartment or suite" />
                    </div>
                  </div>

                  <div class="form-row-grid cols-3">
                    <div class="field" :class="{ 'has-error': errors.state }">
                      <label for="homeState">State</label>
                      <input id="homeState" v-model="currentUserState"
                        class="form-input" :class="fieldClass('state')"
                        type="text" placeholder="State"
                        @blur="touchField('state')" @input="touchField('state')" />
                      <div class="field-error" v-if="errors.state">
                        <font-awesome-icon icon="exclamation-circle" /> State is required
                      </div>
                    </div>
                    <div class="field" :class="{ 'has-error': errors.city }">
                      <label for="homeCity">City</label>
                      <input id="homeCity" v-model="currentUserCity"
                        class="form-input" :class="fieldClass('city')"
                        type="text" placeholder="Hometown"
                        @blur="touchField('city')" @input="touchField('city')" />
                      <div class="field-error" v-if="errors.city">
                        <font-awesome-icon icon="exclamation-circle" /> City is required
                      </div>
                    </div>
                    <div class="field" :class="{ 'has-error': errors.zip }">
                      <label for="homeZip">Zip code</label>
                      <input id="homeZip" v-model="currentUserZip"
                        class="form-input" :class="fieldClass('zip')"
                        type="text" placeholder="90210"
                        @blur="touchField('zip')" @input="touchField('zip')" />
                      <div class="field-error" v-if="errors.zip">
                        <font-awesome-icon icon="exclamation-circle" /> Valid zip code required
                      </div>
                    </div>
                  </div>

                  <div class="form-row-grid cols-2">
                    <div class="field" :class="{ 'has-error': errors.dayPhone }">
                      <label for="daytimePhone">Daytime phone</label>
                      <input id="daytimePhone" v-model="currentUserDayPhone"
                        class="form-input" :class="fieldClass('dayPhone')"
                        type="text" placeholder="(555) 000-0000"
                        @blur="touchField('dayPhone')" @input="touchField('dayPhone')" />
                      <div class="field-error" v-if="errors.dayPhone">
                        <font-awesome-icon icon="exclamation-circle" /> Phone number is required
                      </div>
                    </div>
                    <div class="field">
                      <label for="mobilePhone">Evening phone <span class="opt">(Optional)</span></label>
                      <input id="mobilePhone" v-model="currentUserEveningPhone"
                        class="form-input" type="text" placeholder="(555) 000-0000" />
                    </div>
                  </div>

                  <div class="field-divider"></div>

                  <div class="form-row-grid cols-1">
                    <div class="field" :class="{ 'has-error': errors.email }">
                      <label for="personalEmail">Email address</label>
                      <input id="personalEmail" v-model="currentUserEmail"
                        v-on:input="setOrderEmailAddress(); touchField('email')"
                        class="form-input" :class="fieldClass('email')"
                        type="email" placeholder="you@example.com"
                        @blur="touchField('email')" />
                      <div class="field-error" v-if="errors.email">
                        <font-awesome-icon icon="exclamation-circle" /> Please enter a valid email address
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              <!-- Right: summary -->
              <div class="col-md-3">
                <div class="summary-card">

                  <div class="sc-header">
                    <span class="sc-title">Order summary</span>
                  </div>

                  <div class="sc-rows">
                    <div class="sc-row">
                      <span class="sc-label">Reward to claim</span>
                    </div>
                    <div class="sc-divider"></div>
                    <div class="sc-row">
                      <span class="sc-val" style="font-size:13px;white-space:normal">{{ product.name }}</span>
                    </div>
                  </div>

                  <!-- Sticky on mobile, inline on desktop -->
                  <div class="bp-checkout-sticky">

                    <div class="ack-row" :class="{ 'ack-error': ackTouched && !acknowledged }">
                      <input
                        id="ack-checkbox"
                        type="checkbox"
                        v-model="acknowledged"
                        class="ack-input"
                        @change="onAckChange"
                      />
                      <label for="ack-checkbox" class="ack-label">
                        I acknowledge and agree to the order information.
                      </label>
                    </div>
                    <div class="field-error ack-field-error px-3 pb-2" v-if="ackTouched && !acknowledged">
                      <font-awesome-icon icon="exclamation-circle" />
                      Please acknowledge to continue
                    </div>

                    <div class="sc-action">
                      <button
                        :disabled="isPlacingOrder"
                        class="btn-place-order"
                        v-on:click="handlePlaceOrder"
                      >
                        <span v-if="!isPlacingOrder">Place order</span>
                        <span v-else class="btn-loading">
                          <span class="btn-spinner"></span>
                          Placing order...
                        </span>
                      </button>
                    </div>

                    <router-link class="btn-back-to-cart" :to="{ name: 'Bonus Points' }">
                      ← Back to items
                    </router-link>

                  </div>

                  <div class="sc-note">
                    Rewards are subject to change based on availability and vendor fulfillment.
                  </div>

                </div>
              </div>

            </div>
          </b-form>
        </transition>
      </div>

      <!-- Order info modal -->
      <b-modal
        v-model="showOrderInfoModal"
        title="Important order information"
        ok-only
        ok-title="Got it"
        ok-variant="primary"
        centered
        size="md"
        header-class="order-modal-header"
        body-class="order-modal-body"
      >
        <template #default>
          <div v-html="orderModalContent" class="order-modal-list"></div>
        </template>
      </b-modal>

      <appFooter></appFooter>
    </div>
  </div>
</template>

<script>
import Header from '@/components/Header.vue'
import Footer from '@/components/Footer.vue'
import { mapState } from 'vuex'
import { trim } from 'core-js/internals/string-trim'

export default {
  components: {
    appHeader: Header,
    appFooter: Footer,
  },
  data() {
    return {
      checkoutProgressMax: 100,
      checkoutProgressCurrent: 60,
      newUserEmail: null,
      newUserFirstname: null,
      newUserLastname: null,
      newUserAddress1: null,
      newUserAddress2: null,
      newUserCity: null,
      newUserState: null,
      newUserZip: null,
      newUserDayPhone: null,
      newUserEveningPhone: null,
      // ── new: validation + loader state ──
      isPlacingOrder: false,
      touched: {},
      acknowledged: false,
      ackTouched: false,
      showOrderInfoModal: false,
      orderModalContent: `
        <ul>
          <li><b>All sales are final</b> — orders cannot be changed, canceled, refunded, or returned once submitted.</li>
          <li>Rewards are subject to availability and vendor fulfillment.</li>
          <li>The cost of any prize must, by law, be <b>reported as additional income</b> and will appear on your pay-stub.</li>
        </ul>
      `,
    }
  },
  computed: {
    ...mapState({
      bonusPointsMenuItem: state => state.company.bonusPointsMenuItem,
      userId: state => state.user.profile.id,
      username: state => state.user.profile.username,
      userEmail: state => state.user.profile.email,
      userFirstname: state => state.user.profile.firstName,
      userLastname: state => state.user.profile.lastName,
      userAddress1: state => state.user.profile.address1,
      userAddress2: state => state.user.profile.address2,
      userCity: state => state.user.profile.city,
      userState: state => state.user.profile.state,
      userZip: state => state.user.profile.zip,
      userDayPhone: state => state.user.profile.dayPhone,
      userEveningPhone: state => state.user.profile.eveningPhone,
      product: state => state.product.bonusPointsDetails,
    }),

    // ── validation errors ──
    errors() {
      return {
        firstName: this.touched.firstName && !this.currentUserFirstname?.trim(),
        lastName:  this.touched.lastName  && !this.currentUserLastname?.trim(),
        address1:  this.touched.address1  && !this.currentUserAddress1?.trim(),
        state:     this.touched.state     && !this.currentUserState?.trim(),
        city:      this.touched.city      && !this.currentUserCity?.trim(),
        zip:       this.touched.zip       && !/^\d{5}$/.test((this.currentUserZip || '').trim()),
        dayPhone:  this.touched.dayPhone  && !this.currentUserDayPhone?.trim(),
        email:     this.touched.email     && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.currentUserEmail || ''),
      };
    },

    // ── two-way user fields (UNCHANGED) ──
    currentUserEmail: {
      get() { return this.newUserEmail === null ? this.userEmail : this.newUserEmail },
      set(val) { this.newUserEmail = val },
    },
    currentUserFirstname: {
      get() { return this.newUserFirstname === null ? this.userFirstname : this.newUserFirstname },
      set(val) { this.newUserFirstname = val },
    },
    currentUserLastname: {
      get() { return this.newUserLastname === null ? this.userLastname : this.newUserLastname },
      set(val) { this.newUserLastname = val },
    },
    currentUserAddress1: {
      get() { return this.newUserAddress1 === null ? this.userAddress1 : this.newUserAddress1 },
      set(val) { this.newUserAddress1 = val },
    },
    currentUserAddress2: {
      get() { return this.newUserAddress2 === null ? this.userAddress2 : this.newUserAddress2 },
      set(val) { this.newUserAddress2 = val },
    },
    currentUserCity: {
      get() { return this.newUserCity === null ? this.userCity : this.newUserCity },
      set(val) { this.newUserCity = val },
    },
    currentUserState: {
      get() { return this.newUserState === null ? this.userState : this.newUserState },
      set(val) { this.newUserState = val },
    },
    currentUserZip: {
      get() { return this.newUserZip === null ? this.userZip : this.newUserZip },
      set(val) { this.newUserZip = val },
    },
    currentUserDayPhone: {
      get() { return this.newUserDayPhone === null ? this.userDayPhone : this.newUserDayPhone },
      set(val) { this.newUserDayPhone = val },
    },
    currentUserEveningPhone: {
      get() { return this.newUserEveningPhone === null ? this.userEveningPhone : this.newUserEveningPhone },
      set(val) { this.newUserEveningPhone = val },
    },
  },
  methods: {
    // ── new validation helpers ──
    touchField(field) {
      this.$set(this.touched, field, true);
    },
    fieldClass(field) {
      if (!this.touched[field]) return '';
      return this.errors[field] ? 'is-invalid' : 'is-valid';
    },
    onAckChange() {
      this.ackTouched = true;
      if (this.acknowledged) {
        this.showOrderInfoModal = true;
      }
    },
    handlePlaceOrder() {
      this.ackTouched = true;
      if (!this.acknowledged) return;

      ['firstName', 'lastName', 'address1', 'state', 'city', 'zip', 'dayPhone', 'email']
        .forEach(f => this.$set(this.touched, f, true));

      this.$nextTick(() => {
        const hasErrors = Object.values(this.errors).some(Boolean);
        if (hasErrors) {
          const firstError = document.querySelector('.form-input.is-invalid');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstError.focus();
          }
          return;
        }
        this.isPlacingOrder = true;
        this.orderBonus(); // ← calls original unchanged method
      });
    },

    // ── ALL ORIGINAL METHODS BELOW — UNTOUCHED ──
    setOrderEmailAddress: function () {
      this.$store.dispatch('cart/setOrderEmailAddress', this.currentUserEmail)
    },
    orderBonus: function () {
      if (
        this.newUserFirstname !== null ||
        this.newUserLastname !== null ||
        this.newUserAddress1 !== null ||
        this.newUserAddress2 !== null ||
        this.newUserCity !== null ||
        this.newUserZip !== null ||
        this.newUserState !== null ||
        this.newUserDayPhone !== null ||
        this.newUserEveningPhone !== null
      ) {
        this.updateInformation()
      } else {
        this.$router.push({ name: 'Bonus Order Summary' })
      }
    },
    updateInformation: function () {
      if (!this.isUserDataValid()) {
        this.isPlacingOrder = false;
        return
      }
      this.$store.dispatch('user/setProfile', {
        id: this.userId,
        firstName: this.newUserFirstname,
        lastName: this.newUserLastname,
        address1: this.newUserAddress1,
        address2: this.newUserAddress2,
        city: this.newUserCity,
        zip: this.newUserZip,
        state: this.newUserState,
        dayPhone: this.newUserDayPhone,
        eveningPhone: this.newUserEveningPhone,
      })
        .then(() => {
          this.$router.push({ name: 'Bonus Order Summary' })
        }, () => {
          this.isPlacingOrder = false;
          this.showError('Failed to update profile')
        })
        .catch(() => {
          this.isPlacingOrder = false;
          this.showError('Failed to update profile')
        })
    },
    isUserDataValid: function () {
      return this.currentUserFirstname != null && trim(this.currentUserFirstname).length > 0 &&
        this.currentUserLastname != null && trim(this.currentUserLastname).length > 0 &&
        this.currentUserAddress1 != null && trim(this.currentUserAddress1).length > 0 &&
        this.currentUserCity != null && trim(this.currentUserCity).length > 0 &&
        this.currentUserZip != null && trim(this.currentUserZip).length > 0 &&
        this.currentUserState != null && trim(this.currentUserState).length > 0 &&
        this.currentUserDayPhone != null && trim(this.currentUserDayPhone).length > 0 &&
        this.currentUserEmail != null && trim(this.currentUserEmail).length > 0
    },
    showSuccess: function (message, onClose) {
      this.$bvModal.msgBoxOk(message, {
        size: 'sm', headerClass: 'p-2 border-bottom-0',
        footerClass: 'p-2 border-top-0', centered: true,
      }).then(() => { onClose() })
    },
    showError: function (message) {
      this.$bvModal.msgBoxOk(message, {
        size: 'sm', headerClass: 'p-2 border-bottom-0',
        footerClass: 'p-2 border-top-0', okVariant: 'danger', centered: true,
      })
    },
  },
  created() {
    this.$store.dispatch('cart/fetchCartSummary')
    this.$store.dispatch('user/fetchProfile')
  },
}
</script>

<style lang="scss">

/* ── Stepper ── */
.cart-stepper { display: flex; align-items: center; }
.cs-step { display: flex; align-items: center; gap: 7px; font-size: 13px; color: $gray-500; }
.cs-step.active { color: $primary; font-weight: 500; }
.cs-step.done   { color: $success; }
.cs-dot {
  width: 22px; height: 22px; border-radius: 50%;
  border: 2px solid $gray-300;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 500; background: #fff; color: $gray-500; flex-shrink: 0;
}
.cs-step.active .cs-dot { border-color: $primary; color: $primary; background: lighten($primary, 44%); }
.cs-step.done   .cs-dot { border-color: $success; color: #fff; background: $success; font-size: 9px; }
.cs-line { flex: 1; height: 1px; background: $gray-200; min-width: 20px; }
.cs-line-done { background: $success; }

/* ── Form card ── */
.form-card {
  background: #fff; border: 1px solid $gray-200;
  border-radius: $border-radius; padding: 1.25rem;
}
.form-section-title { font-size: 1rem; font-weight: 500; margin-bottom: .25rem; }
.form-subtitle { font-size: 0.825rem; color: $gray-600; margin-bottom: 1rem; }
.form-section-label {
  font-size: 0.8rem; font-weight: 500; color: $gray-600;
  text-transform: uppercase; letter-spacing: .04em; margin-bottom: .75rem;
}

/* ── Field grid ── */
.form-row-grid {
  display: grid; gap: .85rem; margin-bottom: .85rem;
  &.cols-1 { grid-template-columns: 1fr; }
  &.cols-2 { grid-template-columns: 1fr 1fr; }
  &.cols-3 { grid-template-columns: 2fr 3fr 2fr; }
}

/* ── Fields ── */
.field { display: flex; flex-direction: column; gap: 4px; }
.field label { font-size: 12.5px; font-weight: 500; color: $gray-700; margin: 0; }
.field label .opt { font-weight: 400; color: $gray-600; margin-left: 4px; }
.form-input {
  height: 36px; border: 1px solid $gray-300; border-radius: $border-radius-sm;
  padding: 0 10px; font-size: 13.5px; background: #fff; color: $gray-900;
  outline: none; transition: border-color .15s, box-shadow .15s; width: 100%;
  &:focus { border-color: $primary; box-shadow: 0 0 0 2px rgba($primary, .12); }
  &.is-invalid { border-color: $danger; background: lighten($danger, 47%); }
  &.is-valid   { border-color: $success; }
}
.field-error {
  display: flex; align-items: center; gap: 4px;
  font-size: 11.5px; color: $danger; margin-top: 1px;
}
.field-divider { height: 1px; background: $gray-100; margin: .25rem 0; }

/* ── Warning box ── */
.warn-box {
  background: lighten(#f59e0b, 40%);
  border: 1px solid lighten(#f59e0b, 20%);
  border-radius: $border-radius; padding: 1rem;
}
.warn-row { display: flex; align-items: flex-start; gap: 10px; &:last-child { margin-bottom: 0; } }
.warn-icon {
  width: 28px; height: 28px; border-radius: 50%; background: $gray-600;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 11px; flex-shrink: 0; margin-top: 2px;
}
.warn-text { font-size: 12.5px; color: $gray-700; line-height: 1.5; }

/* ── Summary card ── */
.summary-card {
  background: #fff; border: 1px solid $gray-200;
  border-radius: $border-radius; overflow: hidden; position: sticky; top: 80px;
}
.sc-header { padding: .85rem 1rem; border-bottom: 1px solid $gray-200; }
.sc-title   { font-size: 0.95rem; font-weight: 500; }
.sc-rows    { padding: .25rem 0; }
.sc-row     { display: flex; justify-content: space-between; align-items: flex-start; padding: .55rem 1rem; font-size: 0.825rem; }
.sc-label   { color: $gray-700; }
.sc-val     { font-weight: 500; color: $gray-800; }
.sc-divider { height: 1px; background: $gray-100; margin: 0 1rem; }
.sc-action  { padding: .85rem 1rem .25rem; }
.sc-note    { padding: .65rem 1rem; font-size: 0.72rem; color: $gray-600; line-height: 1.5; border-top: 1px solid $gray-200; }

/* ── Place order button ── */
.btn-place-order {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; background: $primary; border: none;
  border-radius: $border-radius-sm; padding: 11px;
  font-size: 0.9rem; font-weight: 500; color: #fff;
  cursor: pointer; transition: background .15s ease;
  &:hover:not(:disabled) { background: darken($primary, 8%); }
  &:disabled { background: lighten($primary, 25%); cursor: not-allowed; }
}
.btn-spinner {
  display: inline-block; width: 14px; height: 14px;
  border: 2px solid rgba(#fff, .3); border-top-color: #fff;
  border-radius: 50%; animation: spin .65s linear infinite;
}
.btn-loading { display: flex; align-items: center; gap: 7px; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Back link ── */
.btn-back-to-cart {
  display: block; text-align: center; font-size: 13px; color: $primary;
  padding: .5rem; margin: .1rem 0; text-decoration: none;
  &:hover { text-decoration: underline; }
}

/* ── Acknowledgment ── */
.ack-row {
  display: flex; align-items: flex-start; gap: 10px;
  background: #fff; border: 1px solid $gray-200;
  border-radius: $border-radius-sm; padding: .75rem 1rem;
  margin: .5rem 1rem; transition: border-color .15s;
  &.ack-error { border-color: $danger; background: lighten($danger, 47%); }
}
.ack-input {
  margin-top: 2px; width: 16px; height: 16px;
  flex-shrink: 0; cursor: pointer; accent-color: $primary;
}
.ack-label {
  font-size: 13px; color: $gray-800; line-height: 1.5; cursor: pointer; margin: 0;
}
.ack-field-error { margin-bottom: .5rem; }

/* ── Modal ── */
.order-modal-header { padding-bottom: .25rem !important; border-bottom: none !important; }
.order-modal-body   { padding: .25rem 1.5rem 1.25rem !important; }
.order-modal-list {
  text-align: left;
  ul { padding-left: 1.25rem; margin: 0; }
  li {
    padding: .4rem 0; font-size: 14px; line-height: 1.6; color: $gray-700;
    border-bottom: 1px solid $gray-100;
    &:last-child { border-bottom: none; }
  }
  b { font-weight: 700; color: $gray-900; }
}

/* ── Responsive ── */
@media (max-width: 576px) {
  .form-row-grid.cols-3 { grid-template-columns: 1fr 1fr; }
  .form-row-grid.cols-3 > :last-child { grid-column: span 2; }
}

.modal-body { padding: 2rem 2rem 0 2rem !important; }
.btn-primary { background-color: #F8A355 !important; }
.btn-primary:hover { background-color: #C77C37 !important; }

/* ── Mobile: single column fields ── */
@media (max-width: 768px) {
  .form-row-grid.cols-2,
  .form-row-grid.cols-3 {
    grid-template-columns: 1fr !important;
  }

  .form-row-grid.cols-3 > :last-child {
    grid-column: span 1 !important;   // ← override the earlier span 2
  }

  /* Unstick the summary card on mobile — it goes below the form */
  .summary-card {
    position: static !important;
    margin-top: 1rem;
  }
}

/* ── Checkout sticky bar — mobile only ── */
.bp-checkout-sticky {
  /* desktop: inline inside summary card */
  background: #fff;
}

@media (max-width: 768px) {
  .bp-checkout-sticky {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 200;
    background: #fff;
    border-top: 1px solid $gray-200;
    padding: .85rem 1rem .85rem;
    box-shadow: 0 -4px 16px rgba(0,0,0,0.08);
  }

  /* Push page content up so it's not hidden behind sticky bar */
  .col-md-3 .summary-card .sc-note {
    padding-bottom: 180px; // ← space for sticky bar height
  }

  .container-fluid.account {
    padding-bottom: 160px !important;
  }
}


</style>